import { useMemo } from 'react';
import { PieChartPropsBase, SingleValue } from '@/core';
import { BasePanelProps, useDataQueryResult } from '@/plugin-system';
import { PieChartPanelLoaded } from './PieChartPanelLoaded';

/**
 * Types for the pie chart that are only used in the `Viz` variant.
 *
 * @see {@link PieChartPropsBase} for types defined in the schema because they are also shared with
 * the `DashboardPanel` variant.
 */
interface PieChartPropsVizOnly {
  // Currently no Viz only props, but leaving this here, so it is clear where to put them in the future.
}

export interface PieChartProps extends BasePanelProps, PieChartPropsBase, PieChartPropsVizOnly {
  // Extend values from `PieChartPropsBase` below, as needed, to add in jsdoc to help developers.

  /**
   * Whether to show the value, percent or both inside the pie slices
   * @default 'Percent'
   */
  labelFormat?: PieChartPropsBase['labelFormat'];
  /**
   * Whether or not to show the label lines that render the series name outside the pie slices
   * @default true
   */
  showSeriesName?: PieChartPropsBase['showSeriesName'];

  /**
   * The percent value below which data will be aggregated into a single slice
   * @default 3%
   */
  aggregationPercent?: PieChartPropsBase['aggregationPercent'];

  /**
   * The number of decimal places to show in the percent label
   * @default 0
   */
  percentDecimalPlaces?: PieChartPropsBase['percentDecimalPlaces'];
}

export function PieChartPanel(props: PieChartProps) {
  const { width, height } = props;

  const { queryResults, isLoading } = useDataQueryResult({ dataTypeFilter: 'SingleValue' });

  const combinedQueryResults = useMemo(() => {
    const combinedQueryResults = queryResults.reduce<SingleValue[]>((acc, result) => {
      if (result.data?.values) {
        acc.push(...result.data.values);
      }
      return acc;
    }, []);
    combinedQueryResults.sort((a, b) => {
      // First, sort by value in ascending order
      if (a.value !== b.value) {
        return (b.value ?? 0) - (a.value ?? 0);
      }
      // If values are the same, sort alphabetically by name
      return a.name.localeCompare(b.name);
    });
    return combinedQueryResults;
  }, [queryResults]);

  if (width === undefined || height === undefined || isLoading) return null;

  return <PieChartPanelLoaded {...props} data={combinedQueryResults} />;
}
